import config from '../config'
import { authRequest } from '../request'

const base = `${config.mainApi}/sandbox/studio-integrations`

export type StudioIntegrationSandboxAccessMethod = {
  usage: 'CHECK_IN' | 'OPEN_DOOR'
  approach: 'PRESENT_CODE' | 'PRESENT_QR_CODE' | 'SCAN_QR_CODE'
  code?: string
  qrCodeInBase64?: string
}

export type StudioIntegrationSandboxBooking = {
  integratorBookingId: string
  integratorLessonId: string
  userId: number
  status: StudioIntegrationSandboxBookingStatus
  methods: StudioIntegrationSandboxAccessMethod[]
  accessRequestUsage: StudioIntegrationSandboxAccessMethod['usage']
  accessRequestApproach: StudioIntegrationSandboxAccessMethod['approach']
  accessRequestStatus: 'SUCCESSFUL' | 'FAILED' | 'UNKNOWN'
  accessRequestFailureCode: string | null
  accessRequestDelayMillis: number
}

export type StudioIntegrationSandboxAccessScenarioRequest = Pick<
  StudioIntegrationSandboxBooking,
  | 'methods'
  | 'accessRequestUsage'
  | 'accessRequestApproach'
  | 'accessRequestStatus'
  | 'accessRequestFailureCode'
  | 'accessRequestDelayMillis'
>

export type StudioIntegrationSandboxBookingStatus =
  | 'BOOKED'
  | 'CHECKED_IN'
  | 'NO_SHOW'
  | 'COMPLETED'
  | 'CANCELLED'

export type StudioIntegrationSandboxLessonDescription = {
  integratorLessonDescriptionId: string
  name: string
  description: string
  credits: number
  active: boolean
}

export type StudioIntegrationSandboxLesson = {
  integratorLessonId: string
  integratorStudioId: string
  integratorLessonDescriptionId: string
  startDateTime: string
  endDateTime: string
  totalSpotsForClassPass: number
  totalSpots: number
  availableSpots: number
  bookableWindowStarts?: string
  bookableWindowEnds?: string
  lateCancelWindow?: string
  cancelled: boolean
}

export const listStudioIntegrationSandboxBookings = () =>
  authRequest.get<MISSING_TYPE, StudioIntegrationSandboxBooking[]>(`${base}/bookings`)

export const updateStudioIntegrationSandboxAccessScenario = (
  integratorBookingId: string,
  data: StudioIntegrationSandboxAccessScenarioRequest,
) =>
  authRequest.put<MISSING_TYPE, void>(
    `${base}/bookings/${integratorBookingId}/access-scenario`,
    data,
  )

export const updateStudioIntegrationSandboxBookingStatus = (
  integratorBookingId: string,
  status: StudioIntegrationSandboxBookingStatus,
) =>
  authRequest.put<MISSING_TYPE, void>(`${base}/bookings/${integratorBookingId}/status`, { status })

export const listStudioIntegrationSandboxLessonDescriptions = () =>
  authRequest.get<MISSING_TYPE, StudioIntegrationSandboxLessonDescription[]>(
    `${base}/lesson-descriptions`,
  )

export const createStudioIntegrationSandboxLessonDescription = (
  data: Omit<StudioIntegrationSandboxLessonDescription, 'integratorLessonDescriptionId'>,
) => authRequest.post<MISSING_TYPE, void>(`${base}/lesson-descriptions`, data)

export const listStudioIntegrationSandboxLessons = () =>
  authRequest.get<MISSING_TYPE, StudioIntegrationSandboxLesson[]>(`${base}/lessons`)

export const createStudioIntegrationSandboxLesson = (
  data: Omit<StudioIntegrationSandboxLesson, 'integratorLessonId'>,
) => authRequest.post<MISSING_TYPE, void>(`${base}/lessons`, data)

export const syncStudioIntegrationSandboxLessonDescriptions = (externalStudioId: string) =>
  authRequest.post<MISSING_TYPE, void>(`${base}/sync-lesson-descriptions`, null, {
    params: { externalStudioId },
  })

export const syncStudioIntegrationSandboxLessons = (
  externalStudioId: string,
  startDate: string,
  endDate: string,
) =>
  authRequest.post<MISSING_TYPE, void>(`${base}/sync-lessons`, null, {
    params: { externalStudioId, startDate, endDate },
  })
